import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import classNames from 'classnames'

import {
    chooseReserveType,
    chooseTooltipReserveType,
    setPopoverOpenedFlag,
    setPopoverClosedFlag,
} from 'state/actions/toolbar'
import { playCancelClickSound, playHoverSound } from 'state/actions/sounds'
import { toCssClass } from 'helpers'

import Popover from 'components/Popover/Popover.jsx'

import {
    ReserveActivationPopover,
    ReserveButton,
    ReserveTooltip,
} from '../../components'

import {
    groupReservesByLevel,
    getBuildingTypeByReserve,
    getReserveBonus,
    isReserveVisible,
    findFirstActiveReserveInGroup,
} from 'helpers/reserves'

import {
    ACTIVATABLE_IN_BATTLE_ROOM,
    RESERVE_TOOLTIP_ID,
} from './constants.jsx'

import { RESERVE_GROUPS } from 'constants'

import styles from './Toolbar.scss'

export class Toolbar extends React.Component {
    static propTypes = {
        clanReserves: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        buildings: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
        popoverOpened: PropTypes.bool.isRequired,
        popoverReserveType: PropTypes.string,
        tooltipReserveType: PropTypes.string,

        chooseReserveType: PropTypes.func.isRequired,
        chooseTooltipReserveType: PropTypes.func.isRequired,
        setPopoverOpenedFlag: PropTypes.func.isRequired,
        setPopoverClosedFlag: PropTypes.func.isRequired,
        playCancelClickSound: PropTypes.func.isRequired,
        playHoverSound: PropTypes.func.isRequired,
    }

    handleClick(reserveType, e) {
        e.stopPropagation()
        if (this.props.popoverReserveType === reserveType) {
            this.refs.popover.hide()
        } else {
            this.refs.popover.hide()
            this.props.chooseReserveType(reserveType)
            setTimeout(this.showPopover.bind(this), 100, e.currentTarget)
        }
    }
    hidePopover() {
        return this.refs.popover.hide()
    }
    showPopover(target) {
        return this.refs.popover.show(target)
    }
    afterHidePopover() {
        this.props.setPopoverClosedFlag()
        this.props.chooseReserveType(null)
    }
    handleMouseEnter(reserveType, e) {
        e.stopPropagation()
        this.props.chooseTooltipReserveType(reserveType)
    }
    handleMouseLeave(reserveType, e) {
        e.stopPropagation()
        this.props.chooseTooltipReserveType(null)
    }
    componentWillMount() {
        this.props.setPopoverClosedFlag()
        this.props.chooseReserveType(null)
        this.update(this.props)
    }
    componentWillReceiveProps(nextProps) {
        this.update(nextProps)
    }
    update(props) {
        this.clearTimeouts()

        const reservesByLevel = {}
        for (let reserveType of Object.keys(props.clanReserves)) {

            const buildingType = getBuildingTypeByReserve(reserveType, props.settings)
            const building = Object.values(props.buildings).find(value => value.type === buildingType) || null
            const availableReserves = props.clanReserves[reserveType].filter(isReserveVisible)

            reservesByLevel[reserveType] = groupReservesByLevel(availableReserves)
            if (Object.keys(reservesByLevel[reserveType]).length === 0) {
                if (building) {
                    reservesByLevel[reserveType][building.level] = []
                } else {
                    reservesByLevel[reserveType][1] = []
                }
            }
        }

        this.timeoutId = setTimeout(this.update.bind(this, props), 1000)

        this.setState({
            reservesByLevel,
            reserveType: props.reserveType,
            reserveGroups: props.settings.reserve_groups,
            reserveSettings: props.settings.reserves,
        })
    }

    componentWillUnmount() {
        this.clearTimeouts()
    }
    clearTimeouts() {
        if (this.timeoutId) clearTimeout(this.timeoutId)
    }
    render() {
        const groups = []
        const reserveGroups = Object.keys(this.state.reserveGroups)
        for (let group of reserveGroups) {
            let header
            if (group === RESERVE_GROUPS.DISPOSABLE) {
                header = (
                    <div className={styles.header}>
                        <p className={styles.subtitle}>{ACTIVATABLE_IN_BATTLE_ROOM}</p>
                    </div>
                )
            }

            const firstActiveReserveInGroup = findFirstActiveReserveInGroup(
                this.props.clanReserves,
                this.props.settings.reserve_groups[group]
            )

            const buttons = this.state.reserveGroups[group].map(reserveType => {
                const reserves = this.state.reservesByLevel[reserveType]
                const reserveBonus = getReserveBonus(this.state.reserveSettings, reserveType)
                const clickHandler = this.handleClick.bind(this, reserveType)
                const mouseEnterHandler = this.handleMouseEnter.bind(this, reserveType)
                const mouseEnterLeave = this.handleMouseLeave.bind(this, reserveType)
                return (
                    <ReserveButton
                        key={reserveType}
                        handleClick={clickHandler}
                        handleMouseEnter={mouseEnterHandler}
                        handleMouseLeave={mouseEnterLeave}
                        group={group}
                        reserveBonus={reserveBonus}
                        reserveType={reserveType}
                        reserves={reserves}
                        hasActiveReserveInGroup={!!firstActiveReserveInGroup}
                        tooltipId={RESERVE_TOOLTIP_ID}
                    />
                )
            })

            const className = classNames(styles.group, styles[`group__${toCssClass(group)}`])
            groups.push(
                <div className={className} key={group}>
                    {header}
                    <div className={styles.wrapper}>
                        {buttons}
                    </div>
                </div>
            )
        }

        let tooltipComponent
        const isPopoverOpened = this.props.popoverOpened &&
            this.props.popoverReserveType === this.props.tooltipReserveType
        if (!isPopoverOpened || this.props.popoverReserveType !== this.props.tooltipReserveType) {
            tooltipComponent = (
                <ReserveTooltip
                    id={RESERVE_TOOLTIP_ID}
                    reservesByLevel={this.state.reservesByLevel}
                />
            )
        }
        return (
            <div className={styles.base}>
                <div className={styles.holder}>
                    {groups}
                </div>

                <Popover
                    ref='popover'
                    place='top'
                    afterShow={this.props.setPopoverOpenedFlag}
                    afterHide={this.afterHidePopover.bind(this)}
                    playHoverSound={this.props.playHoverSound}
                    playCancelClickSound={this.props.playCancelClickSound}
                >
                    <ReserveActivationPopover hide={this.hidePopover.bind(this)} />
                </Popover>
                {tooltipComponent}
            </div>
        )
    }
}

export const mapStateToProps = ({ clanReserves, settings, buildings, toolbar }) => {
    const { reserveType: popoverReserveType, tooltipReserveType, popoverOpened } = toolbar

    return {
        clanReserves,
        settings,
        buildings,
        popoverOpened,
        popoverReserveType,
        tooltipReserveType,
    }
}

export const mapDispatchToProps = {
    chooseReserveType,
    chooseTooltipReserveType,
    setPopoverOpenedFlag,
    setPopoverClosedFlag,
    playCancelClickSound,
    playHoverSound,
}

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar)



// WEBPACK FOOTER //
// ./containers/Stronghold/components/Toolbar/Toolbar.jsx